/**
 * Safe coercions from the redactable free-string fields the assistant tools
 * return (`riesgo`, `estado`, doc `estado`) to the demo domain unions, plus the
 * matching tone/label/hex lookups. The tool outputs are typed as
 * `Redactable<string>`, so a value can be anything — these helpers narrow to a
 * known key when possible and fall back gracefully (never throw, never index a
 * partial record) so a card can always render.
 */
import type { EstadoDoc, EstadoExpediente, NivelRiesgo } from '../../../lib/phase1';
import { RIESGO_LABEL } from '../../../lib/phase1';
import type { Tone } from '../../ui';
import { docTone, riesgoTone } from '../../compliance';
import { BRAND_HEX, RISK_HEX } from '../../../lib/tokens';

const RIESGO_KEYS: readonly NivelRiesgo[] = ['bajo', 'medio', 'alto', 'sin'];

export function asRiesgo(v: string): NivelRiesgo | null {
  return (RIESGO_KEYS as readonly string[]).includes(v) ? (v as NivelRiesgo) : null;
}

/** Human label for a risk band; falls back to the raw string when unrecognized. */
export function riesgoLabel(v: string): string {
  const key = asRiesgo(v);
  return key ? RIESGO_LABEL[key] : v;
}

/** Pill tone for a risk band; neutral slate when the band isn't recognized. */
export function riesgoToneOf(v: string): Tone {
  const key = asRiesgo(v);
  return key ? riesgoTone(key) : 'slate';
}

/** Ring/bar color for a risk band; brand maroon when the band isn't recognized. */
export function riesgoHex(v: string): string {
  const key = asRiesgo(v);
  return key ? RISK_HEX[key] : BRAND_HEX.primary;
}

const ESTADO_KEYS: readonly EstadoExpediente[] = [
  'Nuevo',
  'En Revisión',
  'Docs Pendientes',
  'Escalado',
  'Aprobado',
  'Activo',
  'Rechazado',
  'Actualización Requerida',
];

export function asEstado(v: string): EstadoExpediente | null {
  return (ESTADO_KEYS as readonly string[]).includes(v) ? (v as EstadoExpediente) : null;
}

const DOC_ESTADO_KEYS: readonly EstadoDoc[] = [
  'Vigente',
  'Aceptado',
  'Por Vencer',
  'Solicitado',
  'Vencido',
  'Bajo Revisión',
  'Recibido',
];

/** Pill tone for a document estado; amber (attention) when unrecognized. */
export function docToneOf(v: string): Tone {
  return (DOC_ESTADO_KEYS as readonly string[]).includes(v) ? docTone(v as EstadoDoc) : 'amber';
}

/** Short es-PA date (e.g. "08 jul") from an ISO string; passes through raw when unparseable. */
export function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es-PA', { day: '2-digit', month: 'short' });
}
